import { useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { GameElementIcon } from '../../shared/GameElementPicker/GameElementIcon';
import { GameElementPicker } from '../../shared/GameElementPicker/GameElementPicker';
import { Icon } from '../../shared/Icon';
import { DateField } from './DateField';
import { JiraHtml } from './JiraHtml';
import { JiraIssueLink } from './JiraIssueLink';
import { ScheduleGiftDialog } from './ScheduleGiftDialog';
import { SubtaskAssigneeChips } from './SubtaskAssigneeChips';
import {
    useWrappingGameElements,
    WrappingElementPicker,
} from './WrappingElementPicker';

import { GameElementDto, useGameElements } from '../../hooks/gameElements';
import {
    JIRA_QUERY_KEYS,
    JiraAttachment,
    JiraComment,
    JiraSubtask,
    useUpdateJiraSubtask,
} from '../../hooks/useJiraApi';

import { formatDateRange, formatShortDate } from '../utils/dates';
import { statusChipColor } from '../utils/status';

interface PlannedGiftPanelProps {
    subtask: JiraSubtask;
    onClose: () => void;
    scheduled: boolean;
    canSchedule: boolean;
    /** Hide the summary title/Jira link when a shared dialog header is present. */
    showHeader?: boolean;
}

export const PlannedGiftPanel = ({
    subtask,
    onClose,
    scheduled,
    canSchedule,
    showHeader = true,
}: PlannedGiftPanelProps) => {
    const { options, isLoading: isWrappingLoading } = useWrappingGameElements();
    const gameElementsQuery = useGameElements();

    const isLoading = isWrappingLoading || gameElementsQuery.isLoading;

    if (isLoading) {
        return (
            <>
                {showHeader && <PanelHeader subtask={subtask} />}
                <DialogContent>
                    <Stack sx={{ alignItems: 'center', gap: 2, py: 4 }}>
                        <CircularProgress />
                        <Typography color="text.secondary">
                            Loading gift plan…
                        </Typography>
                    </Stack>
                </DialogContent>
            </>
        );
    }

    return (
        <LoadedPlannedGiftPanel
            key={subtask.key}
            subtask={subtask}
            onClose={onClose}
            scheduled={scheduled}
            canSchedule={canSchedule}
            showHeader={showHeader}
            wrappingOptions={options}
            gameElements={gameElementsQuery.data?.gameElements ?? []}
        />
    );
};

const LoadedPlannedGiftPanel = ({
    subtask,
    onClose,
    scheduled,
    canSchedule,
    showHeader = true,
    wrappingOptions,
    gameElements,
}: PlannedGiftPanelProps & {
    wrappingOptions: GameElementDto[];
    gameElements: GameElementDto[];
}) => {
    const queryClient = useQueryClient();
    const updateSubtask = useUpdateJiraSubtask();

    const initialWrapping = useMemo(
        () =>
            wrappingOptions.find(
                (el) => el.id === subtask.wrappingGameElementId,
            ) ?? null,
        [wrappingOptions, subtask.wrappingGameElementId],
    );

    const initialContent = useMemo(
        () =>
            gameElements.find((el) => el.id === subtask.contentGameElementId) ??
            null,
        [gameElements, subtask.contentGameElementId],
    );

    const [wrappingElement, setWrappingElement] =
        useState<GameElementDto | null>(initialWrapping);
    const [contentElement, setContentElement] = useState<GameElementDto | null>(
        initialContent,
    );
    const [startTime, setStartTime] = useState<Date | null>(
        subtask.startTime ? new Date(subtask.startTime) : null,
    );
    const [endTime, setEndTime] = useState<Date | null>(
        subtask.endTime ? new Date(subtask.endTime) : null,
    );
    const [isBox, setIsBox] = useState(subtask.giftBox ?? false);
    const [scheduleOpen, setScheduleOpen] = useState(false);

    const dateRange = formatDateRange(subtask.startTime, subtask.endTime);

    const handleSave = async () => {
        await updateSubtask.mutateAsync({
            params: { path: { issueKey: subtask.key } },
            body: {
                wrappingGameElementId: wrappingElement?.id ?? '',
                contentGameElementId: contentElement?.id ?? '',
                startTime: startTime?.toISOString(),
                endTime: endTime?.toISOString(),
                giftBox: isBox,
            },
        });
        await queryClient.invalidateQueries({
            queryKey: JIRA_QUERY_KEYS.searchAll,
        });
        onClose();
    };

    return (
        <>
            <ScheduleGiftDialog
                subtask={subtask}
                open={scheduleOpen}
                onClose={() => {
                    setScheduleOpen(false);
                    onClose();
                }}
            />

            {showHeader && <PanelHeader subtask={subtask} />}

            <DialogContent>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    >
                        <Chip
                            label={subtask.status}
                            size="small"
                            variant="outlined"
                            color={statusChipColor(subtask.statusCategory)}
                            sx={{ height: 22 }}
                        />
                        {scheduled && (
                            <Chip
                                icon={
                                    <Box sx={{ display: 'flex', fontSize: 16 }}>
                                        <Icon name="check_circle" />
                                    </Box>
                                }
                                label="Scheduled"
                                size="small"
                                color="success"
                                sx={{ height: 22 }}
                            />
                        )}
                    </Stack>

                    {subtask.description && (
                        <JiraHtml
                            html={subtask.description}
                            sx={{ color: 'text.secondary' }}
                        />
                    )}

                    <ElementPreview subtask={subtask} />

                    <Divider />

                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Edit plan
                    </Typography>
                    <WrappingElementPicker
                        value={wrappingElement ?? initialWrapping}
                        onChange={setWrappingElement}
                        options={wrappingOptions}
                        margin="dense"
                    />
                    <GameElementPicker
                        label="Content game element"
                        value={contentElement || undefined}
                        onChange={setContentElement}
                        size="small"
                    />
                    <DateField
                        label="Start date"
                        value={startTime}
                        onChange={setStartTime}
                    />
                    <DateField
                        label="End date"
                        value={endTime}
                        onChange={setEndTime}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isBox}
                                onChange={(e) => setIsBox(e.target.checked)}
                            />
                        }
                        label="Gift Box"
                    />

                    <Stack spacing={0.75}>
                        {dateRange && (
                            <Stack
                                direction="row"
                                spacing={0.75}
                                sx={{
                                    alignItems: 'center',
                                    minWidth: 0,
                                    color: 'text.secondary',
                                }}
                            >
                                <Icon name="event" />
                                <Typography variant="caption" noWrap>
                                    {dateRange}
                                </Typography>
                            </Stack>
                        )}
                        <SubtaskAssigneeChips subtask={subtask} />
                    </Stack>

                    <AttachmentsSection
                        attachments={subtask.attachments ?? []}
                    />

                    <CommentsSection comments={subtask.comments ?? []} />
                    {updateSubtask.isError && (
                        <Alert severity="error">
                            Failed to update:{' '}
                            {(updateSubtask.error as { message?: string })
                                ?.message ?? 'Unknown error'}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant={scheduled ? 'outlined' : 'contained'}
                    color={scheduled ? 'success' : 'primary'}
                    disabled={scheduled || !canSchedule}
                    startIcon={
                        <Icon
                            name={
                                scheduled ? 'check_circle' : 'event_available'
                            }
                        />
                    }
                    onClick={() => setScheduleOpen(true)}
                    sx={{ mr: 'auto' }}
                >
                    {scheduled ? 'Scheduled' : 'Schedule gift'}
                </Button>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={updateSubtask.isPending}
                >
                    {updateSubtask.isPending ? (
                        <CircularProgress size={16} />
                    ) : (
                        'Save'
                    )}
                </Button>
            </DialogActions>
        </>
    );
};

const ElementPreview = ({ subtask }: { subtask: JiraSubtask }) => {
    const hasElements =
        !!subtask.contentGameElementId || !!subtask.wrappingGameElementId;

    if (!hasElements) {
        if (!subtask.thumbnail) return null;
        return (
            <Box
                component="img"
                src={subtask.thumbnail}
                alt={subtask.summary}
                sx={{
                    width: '100%',
                    height: 140,
                    objectFit: 'cover',
                    borderRadius: 1.5,
                }}
            />
        );
    }

    const entries: { id: string; label: string }[] = [];
    if (subtask.contentGameElementId) {
        entries.push({ id: subtask.contentGameElementId, label: 'Content' });
    }
    if (subtask.wrappingGameElementId) {
        entries.push({ id: subtask.wrappingGameElementId, label: 'Wrapping' });
    }

    return (
        <Stack direction="row" spacing={2}>
            {entries.map((entry) => (
                <Stack
                    key={entry.label}
                    spacing={0.5}
                    sx={{ alignItems: 'center' }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            display: 'flex',
                            bgcolor: 'grey.100',
                            border: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <GameElementIcon gameElementId={entry.id} size={72} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {entry.label}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
};

const AttachmentRow = ({ attachment }: { attachment: JiraAttachment }) => (
    <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center' }}
        component={Link}
        href={attachment.contentUrl}
        target="_blank"
        rel="noreferrer"
        underline="none"
        color="inherit"
    >
        <Box
            sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                border: 1,
                borderColor: 'divider',
                fontSize: 20,
                color: 'text.secondary',
            }}
        >
            {attachment.thumbnailUrl ? (
                <Box
                    component="img"
                    src={attachment.thumbnailUrl}
                    alt={attachment.filename}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            ) : (
                <Icon name="description" />
            )}
        </Box>
        <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {attachment.filename}
            </Typography>
        </Stack>
        <Box sx={{ display: 'flex', fontSize: 18, color: 'text.secondary' }}>
            <Icon name="download" />
        </Box>
    </Stack>
);

const AttachmentsSection = ({
    attachments,
}: {
    attachments: JiraAttachment[];
}) => {
    if (attachments.length === 0) return null;
    return (
        <>
            <Divider />
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <Box sx={{ display: 'flex', fontSize: 18 }}>
                    <Icon name="attach_file" />
                </Box>
                Attachments ({attachments.length})
            </Typography>
            <Stack spacing={1}>
                {attachments.map((attachment) => (
                    <AttachmentRow
                        key={attachment.id}
                        attachment={attachment}
                    />
                ))}
            </Stack>
        </>
    );
};

const CommentRow = ({ comment }: { comment: JiraComment }) => (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Avatar
            src={comment.author?.avatar ?? undefined}
            alt={comment.author?.name}
            sx={{ width: 28, height: 28, fontSize: 14 }}
        >
            {comment.author?.name?.charAt(0)}
        </Avatar>
        <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {comment.author?.name ?? 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {formatShortDate(comment.created)}
                </Typography>
            </Stack>
            <JiraHtml html={comment.body} />
        </Stack>
    </Stack>
);

const CommentsSection = ({ comments }: { comments: JiraComment[] }) => {
    if (comments.length === 0) return null;
    return (
        <>
            <Divider />
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <Box sx={{ display: 'flex', fontSize: 18 }}>
                    <Icon name="chat" />
                </Box>
                Comments ({comments.length})
            </Typography>
            <Stack spacing={1.5}>
                {comments.map((comment) => (
                    <CommentRow key={comment.id} comment={comment} />
                ))}
            </Stack>
        </>
    );
};

const PanelHeader = ({ subtask }: { subtask: JiraSubtask }) => (
    <DialogTitle
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
        }}
    >
        <Box sx={{ minWidth: 0 }}>{subtask.summary}</Box>
        <JiraIssueLink url={subtask.url} />
    </DialogTitle>
);
